import {ChevronRightIcon, MinusIcon, PlusIcon} from '@heroicons/react/outline';
import {pick} from 'lodash-es';
import {useState} from 'react';

import {
  Badge,
  Button,
  Link,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import useCountryList from '@/hooks/useCountryList';
import {statusColors} from '@/types/Transaction';
import {mapCountryCodeToFlags} from '@/utils/countries';
import {capitalizeWords, formatValues} from '@/utils/dashboard';
import * as formatDate from '@/utils/date';

import TransactionSummary from './Summary';
import type {AfriexProcessorTransaction} from './types';

const ProcessorTxTable = ({items}: {items: AfriexProcessorTransaction[]}) => {
  return (
    <Table className="overflow-hidden">
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-20">&nbsp;</Th>
          <Th className="py-4 text-left">Amount</Th>
          <Th className="py-4 text-left">Asset</Th>
          <Th className="py-4 text-left">Status</Th>
          <Th className="py-4 text-left">Type</Th>
          <Th className="py-4 text-left">Processor</Th>
          <Th className="py-4 text-left">Country</Th>
          <Th className="py-4 text-left">Date Initiated</Th>
          <Th className="w-20">&nbsp;</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <ProcessorTxRow key={index} item={item} />
        ))}
      </TBody>
    </Table>
  );
};

const ProcessorTxRow = ({item}: {item: AfriexProcessorTransaction}) => {
  const {countryList} = useCountryList();
  const [isExpanded, setIsExpanded] = useState(false);
  const transactionStatus = item.status;
  const amount = formatValues(Number(item.amount), 'rate');
  const params = pick(item, ['type', 'processor', 'country']);
  const urlParams = new URLSearchParams(params).toString();

  return (
    <>
      <Tr className="hover:bg-gray-100 transition-colors ease-in-out duration-300">
        <Td className="pr-6 py-4 text-right">
          <Button onClick={() => setIsExpanded(!isExpanded)} variant="unStyled">
            <div
              className={`border-2 border-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${isExpanded && 'bg-cyan-50'}`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4 text-gray-700" />
              ) : (
                <PlusIcon className="w-4 h-4 text-gray-700" />
              )}
            </div>
          </Button>
        </Td>
        <Td className={`font-medium`}>{amount}</Td>
        <Td>{item.asset}</Td>
        <Td>
          <Badge
            label={transactionStatus}
            colorScheme={statusColors[transactionStatus]}
          />
        </Td>
        <Td className={`capitalize`}>
          {capitalizeWords(item.type?.replace(/_/g, ' '))}
        </Td>
        <Td className={`capitalize`}>{item.processor}</Td>
        <Td>
          {item.country} {mapCountryCodeToFlags(countryList)[item.country]}
        </Td>
        <Td>{formatDate.DDMonYYYY(item.createdAt?.toString() ?? '')}</Td>
        <Td>
          <Link
            to={`/processors/transactions/${item.processorTransactionId}?${urlParams}`}
            target="_blank"
            rel="noopener noreferrer"
          >
            <div className="w-5 h-5 flex justify-center text-gray-700 dark:text-gray-300 hover:border rounded-lg hover:border-gray-300 hover:bg-cyan-50 hover:text-cyan-500">
              <ChevronRightIcon className="w-5 h-5" />
            </div>
          </Link>
        </Td>
      </Tr>
      {isExpanded && (
        <Tr enableHover={false}>
          <Td colSpan={9} className="p-0 m-0">
            <div className="flex py-10 px-8 justify-center bg-sky-50 w-full">
              <TransactionSummary data={item} countries={countryList} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default ProcessorTxTable;
